import axios from 'axios';
import { getBackendUrl } from '../config';

/**
 * Estado de sessão fora do ciclo de vida do React.
 *
 * Os interceptors de auth precisam existir ANTES de qualquer componente montar:
 * o React roda os effects dos filhos antes dos pais, então um provider filho
 * (PlanPermissionsContext) dispara requests antes de um effect no AuthProvider
 * conseguir registrar o interceptor — e essas requests saíam sem Authorization,
 * levando 401 (bug corrigido). Por isso token, refresh e logout moram aqui, em
 * escopo de módulo, e não dentro de um hook.
 */

const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';

/** O token pode ter sido gravado JSON-stringificado por versões antigas. */
const parseStored = (raw) => {
  if (!raw) return null;
  try {
    return raw.startsWith('"') ? JSON.parse(raw) : raw;
  } catch {
    return raw;
  }
};

export const readToken = () => parseStored(localStorage.getItem(TOKEN_KEY));

export const readRefreshToken = () => parseStored(localStorage.getItem(REFRESH_TOKEN_KEY));

export const writeTokens = ({ token, refreshToken }) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

export const clearTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

/**
 * Cliente próprio, sem interceptors — o refresh não pode passar pelo interceptor
 * de 401 (recursão). Também evita ciclo de import com services/api.js.
 */
const refreshClient = axios.create({});
refreshClient.interceptors.request.use((config) => {
  const baseURL = (getBackendUrl() || process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  return baseURL ? { ...config, baseURL } : config;
});

let refreshPromise = null;

/**
 * Renova o access token. Single-flight: N requests com 401 simultâneo compartilham
 * um único POST /auth/refresh_token. Sem isso, cada request disparava seu próprio
 * refresh e bastava um falhar (ex.: refreshTokenLimiter) para o logout apagar o
 * token que os outros tinham acabado de renovar.
 *
 * @returns {Promise<string|null>} novo access token, ou null se a sessão morreu.
 */
export const refreshAccessToken = () => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = readRefreshToken();
    if (!refreshToken) return null;

    try {
      const { data } = await refreshClient.post('/auth/refresh_token', { refreshToken });
      if (!data?.token) return null;
      writeTokens({ token: data.token, refreshToken: data.refreshToken });
      return data.token;
    } catch {
      return null;
    }
  })();

  // Libera o slot só depois de resolver, para que refreshes futuros (outro ciclo
  // de expiração) não reusem o resultado deste.
  refreshPromise.finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

let logoutHandler = null;

/**
 * Quantas rotas públicas estão abertas agora (link do mapa, formulário,
 * agendamento, checkout...). `Route` com `isPublic` marca ao montar. Uma
 * sessão vencida guardada no navegador não pode tirar o visitante dessas
 * páginas: elas não precisam de login.
 */
let openPublicRoutes = 0;

export const enterPublicRoute = () => {
  openPublicRoutes += 1;
  return () => {
    openPublicRoutes -= 1;
  };
};

export const isOnPublicRoute = () => openPublicRoutes > 0;

/** O useAuth registra aqui o doLogout (precisa de history/setState do React). */
export const setLogoutHandler = (fn) => {
  logoutHandler = fn;
};

/** Sessão inválida: limpa tokens e delega o redirect ao handler do React. */
export const handleSessionExpired = () => {
  clearTokens();
  if (logoutHandler) {
    logoutHandler();
    return;
  }
  // Sem React montado ainda: fallback duro para o login — menos em página
  // pública, que não precisa dele.
  if (isOnPublicRoute()) return;
  const origin = localStorage.getItem('loginOrigin') || 'default';
  window.location.assign(origin === 'mobile' ? '/mobile-login' : '/login');
};
